import { IcConfirmedBold } from '@assets/icons/24x24';
import {
  Button,
  ButtonGroup,
  Checkmark,
  For,
  Grid,
  HStack,
  Icon,
  ListCollection,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ScrollArea } from '../ScrollArea';
import { Option } from '../select/Select';

type Props<T extends Option> = {
  values: string[];
  collection: ListCollection<T>;
  onChange: (values: string[]) => void;
  noOptionMessage?: string;
};

export default function CheckboxList<T extends Option>({
  values,
  collection,
  noOptionMessage,
  onChange,
}: Readonly<Props<T>>) {
  const { formatMessage } = useIntl();

  if (collection.size === 0) {
    return (
      <Text padding={2} width="100%" textAlign="center">
        {formatMessage({ id: noOptionMessage ?? 'components.select.noOption' })}
      </Text>
    );
  }

  return (
    <ScrollArea backgroundColor="neutral.800" width="478px" border="2px solid" borderColor="neutral.black">
      <VStack gap={3} alignItems="start" width="100%" paddingX={3} maxHeight="416px">
        <ButtonGroup gap={1} paddingTop={3}>
          <Button
            variant="toggle"
            size="toggleS"
            onClick={() => onChange(collection.items.map((item) => item.value))}
            disabled={values.length === collection.size}
          >
            <FormattedMessage id="global.all" />
          </Button>
          <Button variant="toggle" size="toggleS" onClick={() => onChange([])} disabled={values.length === 0}>
            <FormattedMessage id="global.none" />
          </Button>
        </ButtonGroup>
        <Grid templateColumns="repeat(2, 1fr)" gap={3} width="100%" paddingBottom={3}>
          <For each={collection.items}>
            {(item) => (
              <HStack
                key={item.value}
                gap={2}
                cursor="pointer"
                onClick={() => {
                  if (values.includes(item.value)) {
                    onChange(values.filter((e) => e !== item.value));
                  } else {
                    onChange(values.concat(item.value));
                  }
                }}
              >
                <Checkmark height="24px" width="24px" checked={values.includes(item.value)}>
                  <Icon as={IcConfirmedBold} width="24px" height="24px" />
                </Checkmark>
                <Text>{item.label}</Text>
              </HStack>
            )}
          </For>
        </Grid>
      </VStack>
    </ScrollArea>
  );
}
